import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../features/i10n';
import ProductInformationCache from '../persistence/description-cache';
import MsxIcons from '../features/msx-icons';
import ProductLink from './product_link';
import { Fragment } from 'react';
import Tooltip from './documentation/tootip';
import { PRECOMM_STATUS, preferredProductReference } from '../constants';
import CommercializationStatus from './commercialization_status';
import InformationIcon from '../atoms/information-icon';

const TableIslandTitle = styled.tr``;

const TableIslandTitleCell = styled.td`
  background-color: white;
  color: #000000;
  padding: 0px;
  border-top: 1px solid #D9D9D9;
  border-left: 1px solid #D9D9D9;
  border-right: 1px solid #D9D9D9;
  vertical-align: middle;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  ${props => props.hideRow ? `
    border-bottom: 1px solid #D9D9D9;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  ` : ``}
  overflow: hidden;
`;

const TableIslandHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  margin-left: 10px;
  font-weight: 700;
  cursor: pointer;
`;

const TableHeaderCell = styled.td`
  background-color: #F3F3F3;
  color: #626469;
  font-weight: 400;
  font-size: 12px;
  padding: 0px;
  vertical-align: middle;

  :first-of-type {
    border-left: 1px solid #D9D9D9;
  }

  :last-of-type {
    border-right: 1px solid #D9D9D9;
  }
`;

const DescriptionTableHeaderCell = styled(TableHeaderCell)`
  @media (max-width: 450px) {
    display: none;
  }
`;

const NumberHeaderCell = styled(TableHeaderCell)`
  text-align: center;
`;

const ProductsTableCell = styled.td`
  padding: 0px;
  vertical-align: middle;
  background-color: white;

  :first-of-type {
    border-left: 1px solid #D9D9D9;
    ${props => props.fromLastRow ? `border-bottom-left-radius: 8px;` : ``}
  }

  :last-of-type {
    border-right: 1px solid #D9D9D9;
    ${props => props.fromLastRow ? `border-bottom-right-radius: 8px;` : ``}
  }

  ${props => props.fromLastRow ? `border-bottom: 1px solid #D9D9D9;` : ``}
`;

const CellContent = styled.div`
  padding: 10px;
  ${props => props.hideCell ? `padding: 0px 10px; height: 0px` : ``}
`;

const NumberCell = styled(ProductsTableCell)`
font-size: 14px;
text-align: center;
`;

const ProductsTableRow = styled.tr`
  ${props => props.hideRow ? 'visibility: hidden;' : 'border-bottom: 1px solid #D9D9D9;'}
`;

const TableHeaderRow = styled.tr`
  ${props => props.hideRow ? 'visibility: hidden;' : ''}
`;

const ArrowIconWrapper = styled.div`
  user-select: unset;
  transition: transform 0.3s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: content-box;
  min-width: 32px;
  width: 32px;
  max-width: 32px;
  height: 32px;
`;

const IndexLabel = styled.span``;

const DescriptionCellContent = styled(CellContent)`
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
`;

const ProductDescriptionLabel = styled.span`
  font-style: normal;
  color: #626469;
  font-size: 12px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DescriptionCell = styled(ProductsTableCell)`
  position: relative;
  width: 100%;
  @media (max-width: 450px) {
    display: none;
  }
`;

const ProductLinkWrapper = styled.div`
  display: flex;
  font-weight: 700;
  font-size: 12px;
  align-items: center;
`;

const ProductLinkWrapperIcon = styled.div`
  transform: scale(0.8);
  color: rgba(224, 151, 34, 1);
`;

// Hidden cell with expect colspan in order to have some margin between rows
const MarginCell = styled.td`
  padding: 5px;
  vertical-align: middle;
`;

const TooltipContentContainer = styled.div`
  color: #e47f00;
  padding: 10px;
  min-width: 200px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  margin-right: 5px;
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
  margin-left: -8px;
`;

const TooltipTitle = styled.span``;

const TooltipDescription = styled.div`
  word-wrap: break-word;
  white-space: pre-line;
  color: #0f0f0f;
`;

const CountryIconWrapper = styled.div`
  color: #e47f00;
  font-weight: bold;
  margin-left: 5px;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  max-width: 24px;
  max-height: 24px;
  transform: scale(0.8);
`;

const WarningWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const WarningText = styled.span`
  font-weight: bold;
  color: #626469;

  @media (max-width: 500px) {
    display: none;
  }
`;

const TooltipContent = ({ title, message }) => (
  <TooltipContentContainer>
    <TooltipHeader>
      <CountryIconWrapper slot="trigger">
        <InformationIcon size="small"></InformationIcon>
      </CountryIconWrapper>
      <TooltipTitle>{title}</TooltipTitle>
    </TooltipHeader>
    <TooltipDescription>{message}</TooltipDescription>
  </TooltipContentContainer>
);

const ProductAvailabilityTableCell = styled.td`
  position: relative;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: middle;
  ${props => props.fromLastRow ? `border-bottom: 1px solid #D9D9D9;` : ``}
`;

const PreferedProductReference = styled.span`
  color: rgb(160, 164, 168);
  font-weight: bold;
  font-size: 10px;
  white-space: nowrap;
`;

class NewBomIsland extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isOpen: true
    }
  }

  static propTypes = {
    island: PropTypes.object,
    preferredProductReference: PropTypes.string,
    datasheetTemplate: PropTypes.string,
    switchIsland: PropTypes.func,
    isLoading: PropTypes.bool,
    availabilityStatus: PropTypes.object,
    showAvailabilityStatus: PropTypes.bool,
    commercializationStatusMap: PropTypes.object
  }

  aggregatedReferences(references) {
    let maxNumber = 1;
    const map = {};
    references.forEach((referenceObj) => {
      if (map[referenceObj.reference]) {
        let obj = map[referenceObj.reference];
        map[referenceObj.reference] = { ...obj, quantity: obj.quantity + referenceObj.quantity }

      } else {
        map[referenceObj.reference] = {
          order: maxNumber++,
          reference: referenceObj.reference,
          quantity: referenceObj.quantity,
          description: referenceObj.description
        };
      }
    });

    const aggregatedReferences = [];
    Object.keys(map).forEach((referenceID) => {
      aggregatedReferences.push(map[referenceID]);
    });

    aggregatedReferences.sort(function (a, b) { return a.order - b.order });
    return aggregatedReferences;
  }

  renderWarningMessage = (aggregatedReference, isReferencePrecomm) => {
    if (isReferencePrecomm) {
      return (
        <WarningWrapper>
          <WarningText>{I10n.get('precommercialization.tooltip.title')}</WarningText>
          <Tooltip
            toggleElement={<ProductLinkWrapperIcon><InformationIcon></InformationIcon></ProductLinkWrapperIcon>}
            containerElement={
              <TooltipContent
                title={I10n.get('precommercialization.tooltip.title')}
                message={I10n.get('screen.head_device_selector_screen.tooltip')}
              />
            }
            allowTop={true}
          />
        </WarningWrapper>
      );
    }
    return <CommercializationStatus availabilityStatus={this.props.availabilityStatus?.[aggregatedReference.reference]?.isCommercialized} />;
  }

  isReferencePrecomm = (aggregatedReference) => {
    return this.props.commercializationStatusMap[aggregatedReference.reference] === PRECOMM_STATUS;
  }

  renderProductRow = (aggregatedReference, index, hideRow, isLastRow) => {
    const isReferencePrecomm = this.isReferencePrecomm(aggregatedReference);
    return (
      <ProductsTableRow key={index} hideRow={hideRow}>
        <NumberCell fromLastRow={isLastRow}><CellContent hideCell={hideRow}><IndexLabel>{aggregatedReference.order}</IndexLabel></CellContent></NumberCell>
        <ProductsTableCell fromLastRow={isLastRow}>
          <CellContent hideCell={hideRow}>
            <ProductLinkWrapper>
              <ProductLink reference={aggregatedReference.reference}></ProductLink>
            </ProductLinkWrapper>
            {this.props.preferredProductReference && this.props.preferredProductReference !== preferredProductReference.none &&
              <PreferedProductReference>
                {this.props.preferredProductReference === preferredProductReference.ean
                  && I10n.getAndReplace('preferences.ean', ProductInformationCache.getEAN(aggregatedReference.reference) || I10n.get('preferences.reference.not_found'))
                }
                {this.props.preferredProductReference === preferredProductReference.enum
                  && I10n.getAndReplace('preferences.enumber', ProductInformationCache.getENumber(aggregatedReference.reference) || I10n.get('preferences.reference.not_found'))
                }
              </PreferedProductReference>
            }
          </CellContent>
        </ProductsTableCell>
        <DescriptionCell fromLastRow={isLastRow} title={ProductInformationCache.getDescription(aggregatedReference.reference) || aggregatedReference.description}>
          <DescriptionCellContent hideCell={hideRow}>
            <ProductDescriptionLabel>
              {ProductInformationCache.getDescription(aggregatedReference.reference) || aggregatedReference.description}
            </ProductDescriptionLabel>
          </DescriptionCellContent>
        </DescriptionCell>
        <ProductAvailabilityTableCell fromLastRow={isLastRow}>
          <CellContent style={{display: 'flex', justifyContent: 'flex-end' }} hideCell={hideRow}>
            {this.props.showAvailabilityStatus && (this.props.availabilityStatus?.[aggregatedReference.reference] || isReferencePrecomm) && this.renderWarningMessage(aggregatedReference, isReferencePrecomm)}
          </CellContent>
        </ProductAvailabilityTableCell>
        <ProductsTableCell fromLastRow={isLastRow}><CellContent hideCell={hideRow}>{aggregatedReference.quantity}</CellContent></ProductsTableCell>
      </ProductsTableRow>
    );
  };

  render = () => {
    const canSwitchIsland = this.props.switchIsland != null;
    const hideRow = !this.state.isOpen;
    const references = this.aggregatedReferences(this.props.island.references);
    return (
      <Fragment>
        <TableIslandTitle onClick={() => { this.setState({isOpen: !this.state.isOpen}); }}>
          <TableIslandTitleCell hideRow={hideRow} colSpan={5}>
            <TableIslandHeader>
            <span onClick={(event) => {
              if (canSwitchIsland && !this.props.isLoading) {
                event.stopPropagation();
                this.props.switchIsland(this.props.island.islandSlot)
              }
            }}>
              {this.props.island?.islandSlot === 0 ? I10n.get('screen.head_device_selector_screen.plc') : this.props.island.islandName}
            </span>
              <ArrowIconWrapper isOpen={this.state.isOpen}>
                <MsxIcons name="down-arrow" size="nano"></MsxIcons>
              </ArrowIconWrapper>
            </TableIslandHeader>
          </TableIslandTitleCell>
        </TableIslandTitle>
        <>
          <TableHeaderRow hideRow={hideRow}>
            <NumberHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.order')}</CellContent></NumberHeaderCell>
            <TableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.reference')}</CellContent></TableHeaderCell>
            <DescriptionTableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.description')}</CellContent></DescriptionTableHeaderCell>
            <TableHeaderCell><CellContent hideCell={hideRow}></CellContent></TableHeaderCell>
            <TableHeaderCell><CellContent hideCell={hideRow}>{I10n.get('bom.quantity')}</CellContent></TableHeaderCell>
          </TableHeaderRow>
          {references.map((ref, idx) => { return this.renderProductRow(ref, idx, !this.state.isOpen, references.length === (idx + 1)); })}
          {<ProductsTableRow><MarginCell colSpan={5}></MarginCell></ProductsTableRow>}
        </>
      </Fragment>
    );
  }

}

export default NewBomIsland;
